import React, { useState, useEffect, useRef } from "react";
import "@styles/pages/EventForm/summary/EventSummaryPage.scss";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Checkbox,
  useMediaQuery,
  Dialog,
  Select,
  MenuItem,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useTranslation, Trans } from "react-i18next";
import DropdownFilterMenu from "@components/ui/DropdownFilterMenu";
import { getOptionData, getLocationByType } from "@api/commonApi";
import {
  workshopFilterData,
  staticFilterList,
  genericLatestPopularFilterData,
} from "@data/FilterData";
import DoneIcon from "@mui/icons-material/Done";
import { getWorkShopList } from "@api/formApi";
import { getEventList } from "@api/landingApi";
import { useNavigate, useLocation } from "react-router-dom";
import {
  resetEventForm,
  setEventGaType,
} from "@store/eventForm/eventFormSlice";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { enUS } from "date-fns/locale";
import { zhHK } from "date-fns/locale";
import { format } from "date-fns";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CloseIcon from "@mui/icons-material/Close";
import SortIcon from "@mui/icons-material/Sort";
import { gaEvent } from "@utils/gaEvent";

const EventSummaryPage = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const isChinese = i18n.language === "hk";
  const navigate = useNavigate();
  const location = useLocation();
  const memberInfoData = useSelector(
    (state) => state.memberInfo.memberInfoData
  );
  const isMobile = useMediaQuery("(max-width:425px)");
  const datePickerRef = useRef(null);

  const [dateRange, setDateRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: "selection",
    },
  ]);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);

  const [filterOptions, setFilterOptions] = useState({});
  const [selectedValues, setSelectedValues] = useState({});
  const [cardList, setCardList] = useState([]);
  const [visibleCount, setVisibleCount] = useState(9);

  const filterList = workshopFilterData;
  const searchParams = new URLSearchParams(location.search);
  const type = searchParams.get("type");

  useEffect(() => {
    setSelectedValues((prev) => {
      if (type === "smart_academy" && prev.workshop_theme !== 1) {
        return { ...prev, workshop_theme: 1 };
      }
      if (type === "event_registration" && prev.workshop_theme !== null) {
        return { ...prev, workshop_theme: null };
      }
      return prev;
    });
  }, [location.search]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
    }

    if (showDatePicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDatePicker]);

  useEffect(() => {
    const fetchEventList = async () => {
      try {
        const res = await getEventList();
        setCardList(res.data.Data);
      } catch (err) {
        console.log(err);
      }
    };
    fetchEventList();
  }, []);

  useEffect(() => {
    const fetchAllOptions = async () => {
      const result = {};

      for (const filter of filterList) {
        try {
          let res;

          if (filter.type === "workshop_location") {
            // ✅ 使用特殊 API
            res = await getLocationByType({ type: 2 });
          } else {
            res = await getOptionData({ type: filter.type });
          }

          result[filter.type] = res?.data?.Data || [];
        } catch (err) {
          console.error("Failed to fetch", filter.type, err);
        }
      }

      setFilterOptions(result);
    };

    fetchAllOptions();
  }, []);

  const handleFilterChange = (type, selectedItem) => {
    const field =
      filterList.find((f) => f.type === type)?.valueField || "value";

    const nextValue =
      typeof selectedItem === "boolean"
        ? selectedItem // ✅ Switch / Checkbox: true/false
        : selectedItem?.[field] ?? null; // ✅ Dropdown: 取 valueField

    setSelectedValues((prev) => ({
      ...prev,
      [type]: selectedItem?.[field],
    }));

    console.log(`Filter ${type} changed:`, selectedItem);
  };

  // 過濾卡片列表
  const filteredCards = cardList.filter((item) => {
    // 1. Product filter

    if (selectedValues.workshop_product) {
      const product = item.product?.split(",") || [];
      if (!product.includes(String(selectedValues.workshop_product))) {
        return false;
      }
    }

    // 2. Theme filter
    if (selectedValues.workshop_theme) {
      const themes = item.theme?.split(",") || [];
      if (!themes.includes(String(selectedValues.workshop_theme))) {
        return false;
      }
    }

    // 3. Location filter
    if (selectedValues.workshop_location) {
      const locations = item.location_id?.split(",") || [];
      if (!locations.includes(String(selectedValues.workshop_location))) {
        return false;
      }
    }

    // 4. 日期範圍 filter：start_date 是否在選擇區間
    const toDayStart = (d) =>
      new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const toDayEnd = (d) =>
      new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

    // 4. 日期範圍 filter：與用戶所選 dateRange 是否有交集（inclusive）
    if (dateRange[0].startDate && dateRange[0].endDate) {
      const cardStart = toDayStart(new Date(item.start_date)); // e.g. "2025-09-18"
      const cardEnd = toDayEnd(new Date(item.end_date || item.start_date)); // 沒有 end 就用 start 當單日課程

      const filterStart = toDayStart(new Date(dateRange[0].startDate)); // e.g. "2025-09-20"
      const filterEnd = toDayEnd(new Date(dateRange[0].endDate)); // e.g. "2025-09-26"

      const hasOverlap = cardStart <= filterEnd && cardEnd >= filterStart;
      if (!hasOverlap) return false;
    }

    // 5. Member privilege（boolean）
    if (selectedValues.member_privilege === true) {
      const isMemberOnly = String(item.type) === "3"; // 你原本邏輯：type === "3" 代表會員限定
      if (!isMemberOnly) return false; // ✅ 打開開關 → 只顯示會員限定
    }

    return true;
  });

  const sortValue = Number(selectedValues.latest_most_popular);

  const sortedCards = [...filteredCards].sort((a, b) => {
    if (sortValue === 1) {
      return (b.total_enrollment || 0) - (a.total_enrollment || 0);
    } else {
      return Number(b.id) - Number(a.id);
    }
  });

  const visibleCards = sortedCards.slice(0, visibleCount);

  useEffect(() => {
    setVisibleCount(9);
  }, [selectedValues, dateRange]);

  const handleResetAllFilters = () => {
    setSelectedValues({});
    setDateRange([
      {
        startDate: null,
        endDate: null,
        key: "selection",
      },
    ]);
  };

  return (
    <Box className="summary-page-container">
      <Typography className="title">
        {type === "smart_academy" ? (
          <Trans i18nKey="form:smart_academy" />
        ) : (
          <Trans i18nKey="form:event_registration" />
        )}
      </Typography>

      {isMobile ? (
        <>
          <Box className="dialog-mobile-filter-button-container">
            <Button
              className="dropdown-button mobile-filter-button"
              onClick={() => setMobileOpen(true)}
            >
              <FilterAltIcon />
              <Trans i18nKey="generic:filter" />
            </Button>
          </Box>
          <Dialog
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            fullScreen
            PaperProps={{ className: "dialog-filter-container" }}
            sx={{
              "& .MuiDialog-paper": {
                position: "fixed",
                bottom: 0,
                maxHeight: "88vh",
                borderTopLeftRadius: 20,
                borderTopRightRadius: 20,
                border: "2px solid #f5f5f5",
                background: "#fbfbfb",
              },
            }}
          >
            <Box className="dialog-top-container">
              <Typography className="dialog-title">
                {type === "smart_academy" ? (
                  <Trans i18nKey="form:smart_academy" />
                ) : (
                  <Trans i18nKey="form:event_registration" />
                )}
              </Typography>
              <CloseIcon onClick={() => setMobileOpen(false)} />
            </Box>

            <Box className="dialog-content">
              <Typography className="dialog-text">
                <SortIcon className="dialog-icon" />
                <Trans i18nKey="generic:sort" />
              </Typography>
              <Box className="toggle-button-container">
                <ToggleButtonGroup
                  value={selectedValues.latest_most_popular ?? 0}
                  exclusive
                  onChange={(e, newValue) => {
                    if (newValue !== null) {
                      handleFilterChange("latest_most_popular", {
                        value: newValue,
                      });
                    }
                  }}
                >
                  <ToggleButton value={0}>
                    <Trans i18nKey="generic:most_latest" />
                  </ToggleButton>
                  <ToggleButton value={1}>
                    <Trans i18nKey="generic:most_popular" />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Box className="dialog-line"></Box>
              <Typography className="dialog-text">
                <FilterAltIcon className="dialog-icon" />
                <Trans i18nKey="generic:filter" />
              </Typography>
              {filterList.map((filter) => {
                const langKey = filter.displayField?.[i18n.language];
                const valueField = filter.valueField || "value";
                const options = filterOptions[filter.type] || [];

                // 預設值：有 selectedValues 就用，否則取第一個 option 嘅 value
                const currentValue =
                  selectedValues?.[filter.type] ??
                  (options.length > 0 ? options[0][valueField] : "");

                return (
                  <Box className="select-container">
                    <Select
                      className="select-filter"
                      variant="outlined"
                      key={filter.type}
                      value={currentValue}
                      onChange={(e) => {
                        const v = e.target.value;
                        const selectedItem =
                          options.find(
                            (o) => String(o?.[valueField]) === String(v)
                          ) || null;
                        handleFilterChange(filter.type, selectedItem);
                      }}
                      MenuProps={{ PaperProps: { sx: { maxHeight: "20vh" } } }}
                    >
                      {options.map((opt) => (
                        <MenuItem key={opt[valueField]} value={opt[valueField]}>
                          {langKey
                            ? opt[langKey]
                            : opt.label || opt.name || String(opt[valueField])}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                );
              })}
              <Box className="member-privilege-switch-container">
                <PeopleAltIcon
                  className={
                    selectedValues.member_privilege && "selected_member"
                  }
                />
                <Typography>
                  <Trans i18nKey="generic:member_exclusive" /> :
                </Typography>
                <Switch
                  className="member-privilege-switch"
                  checked={!!selectedValues.member_privilege}
                  onChange={(e) =>
                    setSelectedValues((prev) => ({
                      ...prev,
                      member_privilege: e.target.checked,
                    }))
                  }
                  onClick={(e) => e.stopPropagation()} // ✅ 防止重複觸發 Button onClick
                />
              </Box>
              <Box className="dialog-date-range-picker-container">
                <Typography className="date-picker-title">
                  <Trans i18nKey="form:mobile_select_date_range" /> :
                </Typography>
                <DateRangePicker
                  ranges={dateRange}
                  onChange={(ranges) => setDateRange([ranges.selection])}
                  moveRangeOnFirstSelection={false}
                  months={1}
                  direction="horizontal"
                  showMonthAndYearPickers={true}
                  locale={isChinese ? zhHK : enUS}
                  showDateDisplay={true} // ❌ 隱藏 header 顯示
                  startDatePlaceholder={t("form:start_date")}
                  endDatePlaceholder={t("form:end_date")}
                  showPreview={true} // ❌ 不顯示 hover 預覽
                  staticRanges={[]} // ❌ 移除左側快捷範圍
                  inputRanges={[]} // ❌ 移除下方自訂範圍
                  minDate={new Date("2025-01-01")} // 最早可選
                  maxDate={new Date("2027-12-01")} // 最遲可選
                  rangeColors={["#f5b623"]}
                  dateDisplayFormat="yyyy-MM-dd"
                />
              </Box>
              <Box className="dialog-line"></Box>
              <Box className="dialog-button-container">
                <Button
                  className="button dialog-reset-filter-button"
                  onClick={handleResetAllFilters}
                >
                  <Trans i18nKey="generic:reset" />
                  <RestartAltIcon className="reset-icon" />
                </Button>
                <Button
                  className="loading-button"
                  onClick={() => setMobileOpen(false)}
                >
                  <Trans i18nKey="generic:view_results" />
                </Button>
              </Box>{" "}
            </Box>
          </Dialog>
        </>
      ) : (
        <>
          <Box className="filter-container">
            {filterList.map((filter, index) => (
              <DropdownFilterMenu
                key={filter.type}
                options={filterOptions[filter.type] || []}
                displayField={filter.displayField[i18n.language]}
                valueField={filter.valueField}
                lang={i18n.language}
                value={selectedValues[filter.type]}
                onChange={(item) => handleFilterChange(filter.type, item)}
              />
            ))}
            <Button
              className="dropdown-button"
              variant="outlined"
              onClick={() => setShowDatePicker(!showDatePicker)}
            >
              <Trans i18nKey="form:select_date_range" />
              {dateRange[0].startDate && dateRange[0].endDate && (
                <RestartAltIcon
                  className="reset-date-button"
                  onClick={() =>
                    setDateRange([
                      {
                        startDate: null,
                        endDate: null,
                        key: "selection",
                      },
                    ])
                  }
                />
              )}
            </Button>
            <Button
              className="dropdown-button"
              onClick={() =>
                setSelectedValues((prev) => ({
                  ...prev,
                  member_privilege: !prev.member_privilege, // 反轉
                }))
              }
            >
              <span className="member-privilege-label">
                <PeopleAltIcon />
                <Trans i18nKey="generic:member_exclusive" />
              </span>
              <Checkbox
                className="member-privilege-checkbox"
                checked={!!selectedValues.member_privilege}
                onChange={(e) =>
                  setSelectedValues((prev) => ({
                    ...prev,
                    member_privilege: e.target.checked,
                  }))
                }
                onClick={(e) => e.stopPropagation()} // ✅ 防止重複觸發 Button onClick
              />
            </Button>

            {/* 最新/最熱門 */}
            <DropdownFilterMenu
              options={genericLatestPopularFilterData}
              displayField={(item) =>
                isChinese ? item.desc_hk : item.desc_hk_en
              }
              valueField="value"
              lang={i18n.language}
              value={selectedValues.latest_most_popular}
              onChange={(item) =>
                handleFilterChange("latest_most_popular", item)
              }
              icon="sort"
            />
            <Button
              className="dropdown-button reset-filter-button"
              onClick={handleResetAllFilters}
            >
              <Trans i18nKey="generic:reset" />
              <RestartAltIcon className="reset-icon" />
            </Button>
          </Box>
          {showDatePicker && (
            <Box className="date-range-picker-container" ref={datePickerRef}>
              <DateRangePicker
                ranges={dateRange}
                onChange={(ranges) => setDateRange([ranges.selection])}
                moveRangeOnFirstSelection={false}
                months={1}
                direction="horizontal"
                showMonthAndYearPickers={true}
                locale={isChinese ? zhHK : enUS}
                showDateDisplay={false} // ❌ 隱藏 header 顯示
                showPreview={true} // ❌ 不顯示 hover 預覽
                staticRanges={[]} // ❌ 移除左側快捷範圍
                inputRanges={[]} // ❌ 移除下方自訂範圍
                minDate={new Date("2025-01-01")} // 最早可選
                maxDate={new Date("2027-12-01")} // 最遲可選
                rangeColors={["#f5b623"]}
              />
            </Box>
          )}
        </>
      )}
      {sortedCards.length === 0 ? (
        <Typography className="coming-soon-text" variant="h6">
          <Trans i18nKey="home:coming_soon" />
        </Typography>
      ) : (
        <Box className="card-section">
          {visibleCards?.map((item, index) => (
            <Box
              className="card-container"
              key={index}
              onClick={() => {
                type === "smart_academy" &&
                  gaEvent(
                    "clubs_smart_academy",
                    "smart_academy_detail",
                    "smart_academy_item",
                    item.id
                  );
                type === "event_registration" &&
                  gaEvent(
                    "clubs_experience",
                    "experience_detail",
                    "experience_item_1",
                    item.id
                  );
                dispatch(resetEventForm());
                dispatch(setEventGaType(type));
                if (item.type == 1) {
                  navigate(`/event_form?id=${item.id}`);
                }
                if (item.type == 2) {
                  navigate(`/event_form?code=${item.campaign_code}`);
                }
                if (item.type == 3) {
                  if (memberInfoData) {
                    navigate(`/event_form?id=${item.id}`);
                    console.log(memberInfoData);
                  } else {
                    navigate(`/login?redirect_url=/event_form?id=${item.id}`);
                  }
                }
              }}
            >
              <Box className="card-image-container">
                {item.type == 3 && (
                  <>
                    <Box className="card-gradient"> </Box>
                    <Box className="card-type">
                      <PeopleAltIcon className="type-icon" />
                      <Trans i18nKey="form:member_exclusive" />
                    </Box>
                  </>
                )}
                <Box className="card-label">
                  <img
                    className="card-image"
                    alt={index}
                    src={isChinese ? item.thumbnail_hk : item.thumbnail_hk_en}
                  />
                </Box>
              </Box>
              <Box className="detail-container">
                <Typography className="detail-title">
                  {isChinese ? item.title_hk : item.title_hk_en}
                </Typography>

                <Typography className="description">
                  {isChinese ? item.desc_hk : item.desc_hk_en}
                </Typography>
                <Box className="summary-button-container">
                  <Button className="summary-button">
                    <Trans i18nKey="form:register_now" />
                  </Button>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}
      {visibleCount < sortedCards.length && (
        <Box className="button-container">
          <Button
            className="loading-button"
            onClick={() => setVisibleCount((prev) => prev + 9)}
          >
            <Trans i18nKey="form:see_more" />
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default EventSummaryPage;
